// Vendor management page
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Form,
  InputGroup,
  Badge,
  Button,
  Spinner,
  Alert,
  Pagination
} from 'react-bootstrap';

import {
  FaSearch,
  FaFilter,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaChartLine,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
} from 'react-icons/fa';

import PageHeader from '../components/PageHeader';
import ConfirmationModal from '../components/ConfirmationModal';
import vendorService from '../services/vendorService';

// (Removed development mock data; production data comes from `vendorService`)

function VendorPage() {
  // Hook for programmatic navigation
  const navigate = useNavigate();
  // State variables
  const [vendors, setVendors] = useState([]);           // Main data list
  const [loading, setLoading] = useState(false);        // Loading indicator
  const [error, setError] = useState('');               // Error messages
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');   // Search input
  const [statusFilter, setStatusFilter] = useState(''); // Status filter
  const [verifiedFilter, setVerifiedFilter] = useState(''); // Verification filter
  const [currentPage, setCurrentPage] = useState(1);    // Pagination: current page
  const [totalItems, setTotalItems] = useState(0);      // Total count
  const [totalPages, setTotalPages] = useState(1);      // Total pages

  // STATS STATE: Custom counters for dashboard stats
  const [activeCount, setActiveCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [verifiedCount, setVerifiedCount] = useState(0);

  // CONFIGURATION: Pagination settings
  const itemsPerPage = 10;

  // Storage URL helper (no trailing slash)
  const storageBase = (process.env.REACT_APP_STORAGE_URL || '').replace(/\/$/, '');
  const makeSrc = (val) => {
    if (!val) return null;
    if (typeof val !== 'string') return null;
    if (val.startsWith('http') || val.startsWith('//')) return val;
    if (val.startsWith('/')) return storageBase ? `${storageBase}${val}` : val;
    return storageBase ? `${storageBase}/uploads/vendors/${val}` : val;
  };

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        sort_by: 'created_at',
        sort_order: 'desc',
      };

      // If a search query exists, use search endpoint
      if (searchQuery) {
        const searchData = await vendorService.search(searchQuery);
        setVendors(searchData.vendors || []);
        setTotalItems(searchData.vendors?.length || 0);
        setTotalPages(1);
        return;
      }

      // Add filters
      if (statusFilter) params.status = statusFilter;
      if (verifiedFilter === 'verified') params.verified = 'true';
      else if (verifiedFilter === 'unverified') params.verified = 'false';

      const data = await vendorService.getAll(params);

      setVendors(data.vendors || []);
      setTotalItems(data.pagination?.total || 0);
      setTotalPages(data.pagination?.pages || 1);

      // Calculate stats
      const active = data.vendors?.filter(v => v.status === 'active').length || 0;
      const pending = data.vendors?.filter(v => v.status === 'inactive' || v.status === 'pending').length || 0;
      const verified = data.vendors?.filter(v => v.is_verified).length || 0;

      setActiveCount(active);
      setPendingCount(pending);
      setVerifiedCount(verified);

    } catch (err) {
      setError('Failed to load vendors. Please try again.');
      console.error('Fetch error:', err);
      // On error, show an empty dataset (no local mock data)
      setVendors([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter, verifiedFilter]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  // Utility functions

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Map status to Bootstrap badge variant
  const getStatusVariant = (status) => {
    const variants = {
      active: 'success',
      inactive: 'secondary',
      pending: 'warning',
      error: 'danger'
    };
    return variants[status] || 'secondary';
  };

  // Delete a vendor
  const handleDeleteClick = (vendor) => {
    setVendorToDelete(vendor);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!vendorToDelete) return;
    
    setDeleting(true);
    try {
      await vendorService.delete(vendorToDelete.id);
      setVendors(vendors.filter(vendor => vendor.id !== vendorToDelete.id));
      setTotalItems(prev => prev - 1);
      setShowDeleteConfirm(false);
      setVendorToDelete(null);
    } catch (err) {
      setError('Failed to delete vendor');
      console.error('Delete error:', err);
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  // Verify a vendor
  const handleVerify = async (id) => {
    try {
      await vendorService.verify(id);
      // Refresh the vendors list
      fetchVendors();
    } catch (err) {
      setError('Failed to verify vendor');
      console.error('Verify error:', err);
    }
  };

  // Unverify a vendor
  const handleUnverify = async (id) => {
    try {
      await vendorService.unverify(id);
      // Refresh the vendors list
      fetchVendors();
    } catch (err) {
      setError('Failed to unverify vendor');
      console.error('Unverify error:', err);
    }
  };

  // Loading state
  if (loading && vendors.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="px-4">
      {/* PAGE HEADER: Title and subtitle */}
      <PageHeader
        title="Vendor Management"
        subtitle={`${totalItems} vendors • ${activeCount} active • ${verifiedCount} verified`}
      />

      {/* SEARCH AND FILTER ROW */}
      <Row className="g-3 mb-4 align-items-center">
        <Col md={5}>
          {/* SEARCH INPUT */}
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaSearch className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search vendors by name or website..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); // Reset to first page on new search
              }}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          {/* STATUS FILTER */}
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaFilter className="text-muted" />
            </InputGroup.Text>
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1); // Reset to first page on new filter
              }}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </Form.Select>
          </InputGroup>
        </Col>
        <Col md={2}>
          {/* VERIFICATION FILTER */}
          <Form.Select
            value={verifiedFilter}
            onChange={(e) => {
              setVerifiedFilter(e.target.value);
              setCurrentPage(1); // Reset to first page on new filter
            }}
          >
            <option value="">All Vendors</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          {/* ACTION BUTTONS */}

            {/* Referral from user profile is handled in the main app; dashboard does not provide referral UI */}
            <Button
              variant="primary"
               className="w-100"
              onClick={() => navigate('/vendors/new')}
            >
              <FaPlus className="me-2" />Create Vendor
            </Button>
        </Col>
      </Row>

      {/* ERROR ALERT */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* STATISTICS CARDS */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-success bg-opacity-10 rounded me-3">
                  <FaChartLine className="text-success" size={24} />
                </div>
                <div>
                  <p className="text-muted text-uppercase small mb-1">Active Vendors</p>
                  <h4 className="fw-bold mb-0">{activeCount}</h4>
                  <small className="text-muted">{Math.round((activeCount / totalItems) * 100) || 0}% of total</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-warning bg-opacity-10 rounded me-3">
                  <FaClock className="text-warning" size={24} />
                </div>
                <div>
                  <p className="text-muted text-uppercase small mb-1">Pending Vendors</p>
                  <h4 className="fw-bold mb-0">{pendingCount}</h4>
                  <small className="text-muted">Awaiting verification</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-info bg-opacity-10 rounded me-3">
                  <FaCheckCircle className="text-info" size={24} />
                </div>
                <div>
                  <p className="text-muted text-uppercase small mb-1">Verified Vendors</p>
                  <h4 className="fw-bold mb-0">{verifiedCount}</h4>
                  <small className="text-muted">Admin approved</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* DATA TABLE */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              {/* TABLE HEADER */}
              <thead className="bg-light">
                <tr>
                  <th className="fw-semibold">ID</th>
                  <th className="fw-semibold">Vendor</th>
                  <th className="fw-semibold">Owner</th>
                  <th className="fw-semibold">Status</th>
                  <th className="fw-semibold">Verification</th>
                  <th className="fw-semibold text-center">Products</th>
                  <th className="fw-semibold text-center">Employees</th>
                  <th className="fw-semibold">Created</th>
                  <th className="fw-semibold text-end">Actions</th>
                </tr>
              </thead>
              {/* TABLE BODY */}
              <tbody>
                {vendors.length === 0 ? (
                  // EMPTY STATE: Show when no vendors match filters
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <p className="text-muted mb-0">No vendors found</p>
                      <small className="text-muted">Try adjusting your filters or search query</small>
                    </td>
                  </tr>
                ) : (
                  // RENDER VENDORS: Map through data array
                  vendors.map((vendor) => (
                    <tr key={vendor.id}>
                      {/* ID Column */}
                      <td>#{vendor.id}</td>

                      {/* Vendor Name and Website */}
                      <td>
                        <div className="d-flex align-items-center">
                          {(() => {
                            const logoSrc = makeSrc(vendor.logo);
                            return logoSrc ? (
                              <img src={logoSrc} alt="logo" style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 6 }} className="me-3" />
                            ) : (
                              <div style={{ width: 48, height: 48 }} className="me-3 bg-light rounded d-flex align-items-center justify-content-center">
                                <small className="text-muted">Logo</small>
                              </div>
                            )
                          })()}
                          <div>
                            <div className="fw-semibold">{vendor.name}</div>
                            {vendor.website && (
                              <small className="text-muted d-block">
                                <a href={vendor.website} target="_blank" rel="noopener noreferrer">
                                  {vendor.website}
                                </a>
                              </small>
                            )}
                            <small className="text-muted d-block">{vendor.phone1 || ''} {vendor.whatsapp ? `• WhatsApp: ${vendor.whatsapp}` : ''}</small>
                           
                          </div>
                        </div>
                      </td>

                      {/* Owner Column */}
                      <td>
                        <div>
                          <div className="fw-semibold">{vendor.owner?.username}</div>
                          <small className="text-muted">{vendor.owner?.email}</small>
                        </div>
                      </td>

                      {/* Status Column */}
                      <td>
                        <Badge bg={getStatusVariant(vendor.status)}>
                          {vendor.status.charAt(0).toUpperCase() + vendor.status.slice(1)}
                        </Badge>
                      </td>

                      {/* Verification Column */}
                      <td>
                        {vendor.is_verified ? (
                          <Badge bg="success">
                            <FaCheckCircle className="me-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge bg="secondary">
                            <FaTimesCircle className="me-1" />
                            Unverified
                          </Badge>
                        )}
                      </td>

                      {/* Products Count */}
                      <td className="text-center">
                        <Badge bg="primary" pill>
                          {vendor.product_count || 0}
                        </Badge>
                      </td>

                      {/* Employees Count */}
                      <td className="text-center">
                        <Badge bg="info" pill>
                          {vendor.employee_count || 0}
                        </Badge>
                      </td>

                      {/* Created Date Column */}
                      <td>
                        <small className="text-muted">
                          {new Date(vendor.created_at).toLocaleDateString()}
                        </small>
                      </td>

                      {/* Actions Column */}
                      <td className="text-end">
                        <div className="d-flex justify-content-end gap-1">
                          {/* View Button */}
                          <Button
                            variant="link"
                            size="sm"
                            className="text-primary p-1"
                            onClick={() => navigate(`/vendors/${vendor.id}`)}
                            title="View details"
                          >
                            <FaEye />
                          </Button>

                          {/* Edit Button */}
                          <Button
                            variant="link"
                            size="sm"
                            className="text-warning p-1"
                            onClick={() => navigate(`/vendors/${vendor.id}/edit`)}
                            title="Edit vendor"
                          >
                            <FaEdit />
                          </Button>

                          {/* Verification Actions */}
                          {!vendor.is_verified ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="text-success p-1"
                              onClick={() => handleVerify(vendor.id)}
                              title="Verify vendor"
                            >
                              <FaCheckCircle />
                            </Button>
                          ) : (
                            <Button
                              variant="link"
                              size="sm"
                              className="text-secondary p-1"
                              onClick={() => handleUnverify(vendor.id)}
                              title="Unverify vendor"
                            >
                              <FaTimesCircle />
                            </Button>
                          )}

                          {/* Delete Button */}
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-1"
                            onClick={() => handleDeleteClick(vendor)}
                            title="Delete vendor"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            {/* First Page Button */}
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            {/* Previous Page Button */}
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />

            {/* Page Numbers */}
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              // Show first page, last page, and pages near current page
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                // Show ellipsis for gaps
                return <Pagination.Ellipsis key={page} disabled />;
              }
              return null;
            })}

            {/* Next Page Button */}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            {/* Last Page Button */}
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      <ConfirmationModal
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setVendorToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Vendor"
        message={`Are you sure you want to delete "${vendorToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
      />
    </Container>
  );
}

// EXPORT: Make component available for import
export default VendorPage;